import { useCallback, useEffect, useState } from "react";
import { Eye, Mail, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const ContactEditor = ({ client, onSaved }) => {
  const [form, setForm] = useState({ name: client.tag_contact.name, email: client.tag_contact.email });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function save(event) {
    event.preventDefault();
    setBusy(true);
    try {
      await api.put(`/client-portal/admin/clients/${client.id}/tag-contact`, form);
      setError("");
      await onSaved();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="preview-contact-form" onSubmit={save} data-testid={`preview-contact-form-${client.id}`}>
      <input value={form.name} maxLength={120} placeholder="TAG contact name" data-testid={`preview-contact-name-${client.id}`} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      <input value={form.email} type="email" placeholder="contact@theallicockgroup.com" data-testid={`preview-contact-email-${client.id}`} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <button type="submit" className="button button-ghost" data-testid={`preview-contact-save-${client.id}`} disabled={busy}><Save size={13} /> Save</button>
      {error && <small className="form-message error-message">{error}</small>}
    </form>
  );
};

export default function ClientPortalPreviewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [error, setError] = useState("");
  const canEditContact = user?.role === "system_administrator";
  const load = useCallback(async () => {
    try {
      const response = await api.get("/client-portal/admin/clients");
      setClients(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="app-shell" data-testid="client-portal-preview-page">
      <AppSidebar />
      <div className="app-content">
        <AppHeader title="Client view preview" subtitle="Open any client's portal exactly as they see it before you invite them. Every preview is written to the audit log." />
        <main className="page-body">
          {error && <p className="form-message error-message">{error}</p>}
          <section className="compliance-card">
            <div className="section-heading"><div><h2><Eye size={17} /> Clients you can preview</h2><p>Read-only: requests, messages and profile edits are disabled in preview. Clients see no billing, rates, budgets, invoices, pay or internal notes.</p></div></div>
            <div className="credential-table-wrap">
              <table className="exclusion-table preview-table">
                <thead><tr><th>Client</th><th>Client logins</th><th>Evaluations</th><th>Team</th><th>TAG account contact</th><th aria-label="Actions" /></tr></thead>
                <tbody>
                  {clients.map((client) => (
                    <tr key={client.id} data-testid={`preview-client-${client.id}`}>
                      <td><b>{client.name}</b><span>{client.region}</span></td>
                      <td>{client.client_logins}</td>
                      <td>{client.evaluations}</td>
                      <td>{client.team}</td>
                      <td>{canEditContact ? <ContactEditor client={client} onSaved={load} /> : <><Mail size={12} /> {client.tag_contact.name} · {client.tag_contact.email}</>}</td>
                      <td><button type="button" className="button button-primary preview-open-button" data-testid={`preview-open-${client.id}`} onClick={() => navigate(`/client-portal-preview/${client.id}`)}><Eye size={14} /><span className="preview-open-label">Preview as {client.name}</span></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
